import i18n from 'i18n';
import * as Yup from 'yup';
import { PAYMENT_PROVIDERS } from 'components/common/constants';
import { formatAccountNumber, isCryptoProvider, passwordRegExp } from 'services/utils';
import moment from 'moment-timezone';

const convertUTCtoTimeZone = (utcDate, timezone, timeLocale = '') => {
  const momentUtcDate = moment.utc(utcDate);
  const convertedGBDate = moment.tz(momentUtcDate, timezone);

  const date = moment(convertedGBDate).format('D MMM YYYY');
  const time = moment(convertedGBDate).format('HH:mm') + (timeLocale ? ` ${timeLocale}` : '');

  return { date, time };
};

export const convertUTCtoLocal = (utcDate) => {
  return convertUTCtoTimeZone(utcDate, moment.tz.guess());
};

export const convertUTCToTransactionsContextTimeZone = (utcDate) => {
  return convertUTCtoLocal(utcDate);
};

export const parseDate = (data) => {
  return convertUTCtoLocal(data);
};

export const getAccountTitle = (account) => {
  if (!account) {
    return '';
  }

  return account?.transfer_provider === PAYMENT_PROVIDERS.WALLESTER
    ? i18n.getMessage('account.title.cardAccount', { accountNumber: account.wallet_number })
    : account?.transfer_provider === PAYMENT_PROVIDERS.MANUAL ||
        account?.transfer_provider === PAYMENT_PROVIDERS.H_AND_H ||
        account?.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
      ? i18n.getMessage('account.title.manual', { accountNumber: account.wallet_number })
      : isCryptoProvider(account?.transfer_provider)
        ? account?.crypto_address
        : account.multi_wallet_number
          ? account.multi_iban?.iban || account.multi_wallet_number
          : account.iban?.iban || account.wallet_number;
};

export const getAccountTitleWithFormatting = (account) => {
  if (!account) {
    return '';
  }

  return account?.transfer_provider === PAYMENT_PROVIDERS.WALLESTER
    ? i18n.getMessage('account.title.cardAccount', { accountNumber: account.wallet_number })
    : account?.transfer_provider === PAYMENT_PROVIDERS.MANUAL ||
        account?.transfer_provider === PAYMENT_PROVIDERS.H_AND_H ||
        account?.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
      ? i18n.getMessage('account.title.manual', { accountNumber: account.wallet_number })
      : isCryptoProvider(account?.transfer_provider)
        ? formatAccountNumber(account?.crypto_address)
        : account.multi_wallet_number
          ? formatAccountNumber(account.multi_iban?.iban || account.multi_wallet_number)
          : formatAccountNumber(account.iban?.iban || account.wallet_number);
};

export const getAccountNumber = (account) => {
  if (!account) {
    return '';
  }

  return account?.transfer_provider === PAYMENT_PROVIDERS.WALLESTER
    ? account.wallet_number
    : account?.transfer_provider === PAYMENT_PROVIDERS.MANUAL ||
        account?.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
        account?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
      ? account.wallet_number
      : account.multi_wallet_number
        ? account.multi_iban?.iban || account.multi_wallet_number
        : account.iban?.iban || account.wallet_number;
};

export const getAccountsOptionsForSelect = (accounts) =>
  accounts?.map((account) => {
    const title = getAccountTitleWithFormatting(account);

    return {
      key: account?.wallet_number,
      value: title,
      currency: account?.currency,
      networkCode: account?.network
    };
  });

export const extractBaseUrl = (url) => {
  const parts = url.split('/');
  return `/${parts.slice(1, 2).join('/')}`;
};

export const getPasswordValidation = () => {
  return Yup.string()
    .nullable(true)
    .min(7, i18n.getMessage('error.PASSWORD_REQUIREMENTS'))
    .max(30, i18n.getMessage('error.PASSWORD_REQUIREMENTS'))
    .required(i18n.getMessage('error.passwordEmpty'))
    .matches(passwordRegExp, { message: i18n.getMessage('error.PASSWORD_REQUIREMENTS') });
};

export const getRegistrationPasswordValidation = () => {
  return Yup.string()
    .nullable(true)
    .required(i18n.getMessage('error.passwordEmpty'))
    .test(
      'valid-characters',
      i18n.getMessage('error.REGISTRATION_PASSWORD_REQUIREMENTS'),
      (value) => !/[^\x20-\x7E]/.test(value)
    );
};

export const isFullSecurityCode = (code) => {
  const regex = /^\w{6}$/;
  return regex.test(code);
};

export const hasMultipleCurrencies = (userAccounts) => {
  if (userAccounts.length < 2) return false;

  const uniqueCurrencies = new Set();

  for (const account of userAccounts) {
    uniqueCurrencies.add(account.currency);
  }

  return uniqueCurrencies.size > 1;
};

export const saveAllParamsToLocalStorage = (params) => {
  const allParams = Array.from(params.entries()).reduce((accumulator, [key, value]) => {
    accumulator[key] = value;
    return accumulator;
  }, {});
  localStorage.setItem('allParams', JSON.stringify(allParams));
};

export const getAllParamsFromLocalStorage = () => {
  const storedParams = localStorage.getItem('allParams');
  return storedParams ? JSON.parse(storedParams) : {};
};

export const clearAllParamsFromLocalStorage = () => {
  localStorage.removeItem('allParams');
};

export const base64ToImageUrl = (base64Data) => {
  return 'data:image/png;base64,' + base64Data;
};

export const getFirstCreatedAccountDate = (accounts) => {
  if (!accounts || accounts.length === 0) {
    return undefined;
  }

  let earliestDate = new Date(accounts[0].created_at);

  accounts.forEach((account) => {
    const accountDate = new Date(account.created_at);
    if (accountDate < earliestDate) {
      earliestDate = accountDate;
    }
  });

  return earliestDate;
};

export const PERMANENT_STORAGE_KEY = 'permanentStorage';

export const saveInPermanentStorage = (key, value) => {
  try {
    let permanentStorage = localStorage.getItem(PERMANENT_STORAGE_KEY);
    if (permanentStorage) {
      permanentStorage = JSON.parse(permanentStorage);
    } else {
      permanentStorage = {};
    }
    permanentStorage[key] = value;
    localStorage.setItem(PERMANENT_STORAGE_KEY, JSON.stringify(permanentStorage));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`setting ${key} not saved in ${PERMANENT_STORAGE_KEY}`, e);
  }
};

export const getFromPermanentStorage = (key) => {
  try {
    let permanentStorage = localStorage.getItem(PERMANENT_STORAGE_KEY);
    if (!permanentStorage || permanentStorage === 'null') {
      return null;
    }
    permanentStorage = JSON.parse(permanentStorage);
    if (key in permanentStorage) {
      return permanentStorage[key];
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`cannot get ${key} from ${PERMANENT_STORAGE_KEY}, returning null`, e);
    return null;
  }
};

const DEFAULT_EXPIRATION_SECONDS = 60;

export const getTimerDurationSecondsLeft = (expiresAt) => {
  if (!expiresAt) return DEFAULT_EXPIRATION_SECONDS;

  const fixedExpiresAt = expiresAt.endsWith('Z') ? expiresAt : expiresAt + 'Z';
  const expiresDate = new Date(fixedExpiresAt);

  if (isNaN(expiresDate.getTime())) {
    return DEFAULT_EXPIRATION_SECONDS;
  }

  const currentDate = new Date();
  const diffSec = Math.floor((expiresDate - currentDate) / 1000);

  return Math.max(diffSec, DEFAULT_EXPIRATION_SECONDS);
};

// Function to clean phone number - remove spaces, parentheses, keeping only digits and +
export const cleanPhoneNumber = (phone) => {
  return phone.replace(/[\s()]/g, '');
};
